// src/components/InvGateTicketCard.jsx
import React, { useState } from 'react';
import { Monitor, Code, Network, HelpCircle, CheckCircle2, XCircle } from 'lucide-react';
import { Clock, Settings, User, Layers, RotateCw } from './icons';
import { useNavigate } from 'react-router-dom';

export default function InvGateTicketCard({ ticket, onResponderDerivacion, onDeleteTicket, onAutoasignar, puedeAutoasignar }) {
  const navigate = useNavigate();
  const [showMenu, setShowMenu] = useState(false);

  // Helper para calcular tiempos relativos y SLA
  const formatTimeAgo = (dateString) => {
    if (!dateString) return 'hace un momento';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'ahora';
    if (diffMins < 60) return `hace ${diffMins} min`;
    if (diffHours < 24) return `hace ${diffHours} h`;
    return `hace ${diffDays} d`;
  };

  // Prioridad con colores exactos estilo InvGate
  const getPriorityBadge = (prioridad) => {
    const p = (prioridad || '').toLowerCase();
    if (p === 'crítica' || p === 'critica' || p === 'urgent') {
      return { label: 'Urgente', bg: 'bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-400 border-red-200 dark:border-red-800' };
    }
    if (p === 'alta' || p === 'high') {
      return { label: 'Alta', bg: 'bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400 border-amber-200 dark:border-amber-800' };
    }
    if (p === 'media' || p === 'medium') {
      return { label: 'Media', bg: 'bg-yellow-50 text-yellow-800 dark:bg-yellow-950/40 dark:text-yellow-400 border-yellow-200 dark:border-yellow-800' };
    }
    return { label: 'Baja', bg: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800' };
  };

  // Header tipo InvGate (AGENTE / APROBACIÓN / SIN ASIGNAR)
  const getHeaderPill = () => {
    if (ticket.estado === 'Pendiente' && !ticket.asignadoA) {
      return { text: 'SIN ASIGNAR', bg: 'bg-indigo-600 text-white' };
    }
    if (ticket.estado === 'Esperando Aprobacion' || ticket.esDerivacion) {
      return { text: 'APROBACIÓN', bg: 'bg-blue-600 text-white' };
    }
    if (ticket.asignadoA?.nombre) {
      const name = ticket.asignadoA.nombre.toUpperCase();
      const lastName = ticket.asignadoA.apellido ? ` ${ticket.asignadoA.apellido[0]}.` : '';
      return { text: `${name}${lastName}`, bg: 'bg-amber-700 text-white' };
    }
    return { text: 'AGENTE', bg: 'bg-red-700 text-white' };
  };

  // Icono de categoría
  const getCategoryIcon = (cat) => {
    const c = (cat || '').toLowerCase();
    if (c.includes('hardware') || c.includes('equipo')) return <Monitor className="w-3.5 h-3.5" />;
    if (c.includes('software') || c.includes('app') || c.includes('sistema')) return <Code className="w-3.5 h-3.5" />;
    if (c.includes('red') || c.includes('wifi') || c.includes('internet')) return <Network className="w-3.5 h-3.5" />;
    return <Layers className="w-3.5 h-3.5" />;
  };

  const headerPill = getHeaderPill();
  const priorityBadge = getPriorityBadge(ticket.prioridad);

  // El ticket siempre trae nombreSolicitante (campo obligatorio, quien reporta
  // el problema, con o sin cuenta en HelpDesk). creadoPor solo existe si quien
  // lo creo tiene cuenta de usuario -- se prioriza por ser el dato mas "vivo"
  // (ej. si le cambiaron el nombre despues), pero nombreSolicitante es el
  // fallback real, no un texto generico.
  const requesterName = ticket.creadoPor?.nombre
    ? `${ticket.creadoPor.nombre} ${ticket.creadoPor.apellido || ''}`.trim()
    : (ticket.nombreSolicitante || 'Solicitante desconocido');

  const requesterInitial = requesterName.charAt(0).toUpperCase();
  const requesterDept = ticket.departamento || ticket.empresa || 'General';

  // Tiempo transcurrido desde la creacion (no hay un campo real de "tiempo de
  // resolucion restante" calculado en el backend para tickets no resueltos,
  // asi que se muestra el tiempo transcurrido en vez de inventar un numero).
  // El SLA se pausa mientras el ticket espera al usuario: se descuentan las
  // pausas ya acumuladas y, si esta esperando ahora, el contador se congela
  // en el momento en que paso a "Esperando Usuario".
  const formatDuration = (dateString) => {
    if (!dateString) return null;
    const esperando = ticket.estado === 'Esperando Usuario';
    const fin = esperando && ticket.fechaEsperandoUsuario ? new Date(ticket.fechaEsperandoUsuario) : new Date();
    const diffMs = fin - new Date(dateString) - (ticket.tiempoPausadoMinutos || 0) * 60000;
    const diffMins = Math.max(0, Math.floor(diffMs / 60000));
    const h = Math.floor(diffMins / 60);
    const m = diffMins % 60;
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
  };

  const hasFirstResponse = ticket.comentarios?.length > 0 || ticket.estado !== 'Pendiente';
  // Resuelto por soporte y a la espera de que el usuario confirme (queda en 'Esperando Usuario')
  const esperandoConfirmacion = ticket.estado === 'Esperando Usuario' && !!ticket.fechaResolucion && !!ticket.fechaProgramacionCierre;
  const isResolved = ticket.estado === 'Resuelto' || ticket.estado === 'Cerrado' || esperandoConfirmacion;
  const tiempoTranscurrido = formatDuration(ticket.fechaCreacion);

  // No hay un campo "updatedAt" real en el ticket -- la actividad mas
  // reciente disponible es el ultimo comentario (ya viene ordenado, el
  // mas nuevo primero) o, si no hay ninguno, la fecha de creacion.
  const ultimaActividad = ticket.comentarios?.[0]?.fechaCreacion || ticket.fechaCreacion;

  return (
    <div
      onClick={() => navigate(`/tickets/${ticket.id}`)}
      className="bg-white dark:bg-gray-850 rounded-xl border border-gray-200 dark:border-gray-750 hover:border-blue-400 dark:hover:border-blue-500 shadow-xs hover:shadow-md transition-all duration-200 cursor-pointer flex flex-col justify-between overflow-hidden group relative"
    >
      {/* 1. Header de la Tarjeta (InvGate Top Strip) */}
      <div className="p-3.5 pb-2 flex items-center justify-between border-b border-gray-100 dark:border-gray-800">
        <div className="flex items-center gap-2">
          {/* Tag de Asignado / Approval */}
          <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded text-[11px] font-bold tracking-wider uppercase shadow-xs ${headerPill.bg}`}>
            <span className="text-[10px]">▾</span>
            <span className="truncate max-w-[110px]">{headerPill.text}</span>
            <Clock className="w-2.5 h-2.5 ml-0.5 opacity-80" />
          </span>

          {/* Badge de Prioridad */}
          <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold border ${priorityBadge.bg}`}>
            {priorityBadge.label}
          </span>
        </div>

        {/* Botón de Ajustes / Engranaje */}
        <div className="relative" onClick={(e) => e.stopPropagation()}>
          <button
            onClick={() => setShowMenu(!showMenu)}
            className="p-1 rounded-md text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            title="Opciones del ticket"
          >
            <Settings className="w-4 h-4" />
          </button>

          {/* Menú flotante del engranaje */}
          {showMenu && (
            <div className="absolute right-0 mt-1 w-44 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 z-20 text-xs text-gray-700 dark:text-gray-200">
              <button
                onClick={() => { setShowMenu(false); navigate(`/tickets/${ticket.id}`); }}
                className="w-full text-left px-3 py-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
              >
                Ver Detalle Completo
              </button>
              {onDeleteTicket && (
                <button
                  onClick={(e) => { setShowMenu(false); onDeleteTicket(e, ticket.id); }}
                  className="w-full text-left px-3 py-1.5 hover:bg-red-50 dark:hover:bg-red-900/30 text-red-600 dark:text-red-400 flex items-center gap-2"
                >
                  Eliminar Ticket
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* 2. Cuerpo: Título y Categoría */}
      <div className="p-3.5 py-3 flex-1 flex flex-col justify-between">
        <div>
          {/* Título Principal -- los tickets no tienen un campo "asunto"
              propio, solo descripcion; se usa esa como titulo de la tarjeta */}
          <h3 className="text-sm font-bold text-gray-900 dark:text-white leading-snug line-clamp-2 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
            {ticket.descripcion || 'Sin descripción'}
          </h3>

          {/* Breadcrumb de Servicio / Categoría */}
          <div className="flex items-center gap-1.5 mt-2 text-xs text-gray-500 dark:text-gray-400">
            <span className="text-gray-600 dark:text-gray-300">
              {getCategoryIcon(ticket.categoria)}
            </span>
            <span className="truncate">
              {ticket.categoria || 'Soporte General'}
              {ticket.subcategoria?.nombre ? ` > ${ticket.subcategoria.nombre}` : ''}
            </span>
          </div>
        </div>

        {/* 3. Barra de Indicadores SLA y Tiempos */}
        <div className="mt-3 pt-2.5 border-t border-gray-100 dark:border-gray-800 flex items-center justify-between text-[11px] text-gray-500 dark:text-gray-400">
          <div className="flex items-center gap-3">
            {/* Indicador de Primera Respuesta */}
            <div className="flex items-center gap-1">
              <span className="font-semibold text-gray-600 dark:text-gray-300">F. Resp.:</span>
              {hasFirstResponse ? (
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
              ) : (
                <XCircle className="w-3.5 h-3.5 text-red-400" />
              )}
            </div>

            {/* Indicador de Resolución */}
            <div className="flex items-center gap-1">
              <span className="font-semibold text-gray-600 dark:text-gray-300">Res.:</span>
              {isResolved ? (
                <span className="px-1.5 py-0.2 rounded font-bold bg-emerald-100 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300 text-[10px]">
                  ✓ Resuelto
                </span>
              ) : (
                <span className="px-1.5 py-0.2 rounded font-bold bg-blue-100 text-blue-700 dark:bg-blue-950/50 dark:text-blue-300 text-[10px]">
                  {tiempoTranscurrido || 'En curso'}
                  {ticket.estado === 'Esperando Usuario' ? ' · en pausa' : ''}
                </span>
              )}
            </div>
          </div>

          {/* Timestamp relativo */}
          <div className="flex items-center gap-1 text-gray-400 text-[10px]">
            <RotateCw className="w-2.5 h-2.5" />
            <span>{formatTimeAgo(ultimaActividad)}</span>
          </div>
        </div>
      </div>

      {/* 4. Footer de la Tarjeta: Solicitante y Número de Ticket */}
      <div className="px-3.5 py-2.5 bg-gray-50/80 dark:bg-gray-800/60 border-t border-gray-100 dark:border-gray-800 flex items-center justify-between">
        <div className="flex items-center gap-2 min-w-0">
          {/* Avatar con inicial */}
          <div className="w-7 h-7 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/60 dark:text-amber-300 flex items-center justify-center font-bold text-xs shrink-0 ring-1 ring-amber-300 dark:ring-amber-700">
            {requesterInitial}
          </div>
          <div className="flex flex-col min-w-0">
            <span className="text-xs font-semibold text-gray-900 dark:text-white truncate leading-tight">
              {requesterName}
            </span>
            <span className="text-[10px] text-gray-500 dark:text-gray-400 truncate leading-tight">
              {requesterDept}
            </span>
          </div>
        </div>

        {/* Autoasignarme rápido (solo tickets sin responsable) */}
        {onAutoasignar && !ticket.asignadoA && !ticket.asignadoAId && !isResolved && (puedeAutoasignar ? puedeAutoasignar(ticket) : true) && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onAutoasignar(e, ticket.id); }}
            title="Asignarme este ticket"
            className="mx-2 shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold bg-indigo-600 text-white hover:bg-indigo-700 transition-colors"
          >
            <User className="w-3 h-3" /> Asignármelo
          </button>
        )}

        {/* Badge del Número de Ticket #ID */}
        <span className="text-xs font-bold text-gray-600 dark:text-gray-300 bg-white dark:bg-gray-700 px-2 py-0.5 rounded border border-gray-200 dark:border-gray-600 shadow-2xs shrink-0">
          #{ticket.id}
        </span>
      </div>
    </div>
  );
}
